/**
 * Discriminated union of every frontend-emitted PostHog event.
 *
 * The discriminator field is ``event_name`` (a literal string per
 * schema). TypeScript narrows the property bag to the right per-event
 * shape once the call site selects an ``event_name``; Zod validates
 * the same contract at runtime.
 *
 * Adding a new event: add the Zod schema in the right per-area file,
 * append it to that file's ``...Events`` array, ensure the area is
 * already spread below. No change to this file is needed unless you
 * add a brand-new area (which also needs a new schema file).
 *
 * Plan-doc anchors
 * ----------------
 * - ``plans/PLAN_POSTHOG_02_EVENT_CATALOG.md`` § "Discriminated union
 *   pattern"
 * - ``plans/PLAN_POSTHOG_05_GOVERNANCE.md`` § "Enforcement points"
 *   (TypeScript compile + runtime Zod)
 */

import { z } from "zod";

import { accountEvents } from "./schemas/account";
import { adminEvents } from "./schemas/admin";
import { authEvents } from "./schemas/auth";
import { bankingEvents } from "./schemas/banking";
import { distributionEvents } from "./schemas/distribution";
import { marketingEvents } from "./schemas/marketing";
import { platformEvents } from "./schemas/platform";
import { retailEvents } from "./schemas/retail";

export const ALL_EVENT_SCHEMAS = [
  ...authEvents,
  ...adminEvents,
  ...accountEvents,
  ...platformEvents,
  ...marketingEvents,
  ...retailEvents,
  ...distributionEvents,
  ...bankingEvents,
] as const;

// ``z.discriminatedUnion`` requires at least two variants. The Phase-1
// catalogue ships well over that, so the cast here is purely to satisfy
// the tuple-typed signature without an "as any" — the runtime shape is
// identical to the spread above.
export const EventSchema = z.discriminatedUnion(
  "event_name",
  ALL_EVENT_SCHEMAS as unknown as readonly [
    (typeof ALL_EVENT_SCHEMAS)[number],
    (typeof ALL_EVENT_SCHEMAS)[number],
    ...(typeof ALL_EVENT_SCHEMAS)[number][],
  ],
);

/**
 * The input shape — what call sites pass to ``captureEvent``. Zod's
 * transforms (e.g. ``TickerProperty.transform(s => s.toUpperCase())``)
 * mean the input type and output type differ slightly; ``z.input``
 * is the right one for the public API.
 */
export type Event = z.input<typeof EventSchema>;

/** The parsed/normalised shape — what ``capture()`` sends to PostHog. */
export type ParsedEvent = z.output<typeof EventSchema>;
